import React, {
  useMemo,
  useEffect,
  useRef,
  useLayoutEffect,
  useState,
} from 'react';
import { formatPct } from '../dummyData';
import { SubscriptionSummary } from '../../../../types/subscription';
import { useDispatch } from 'react-redux';
import { setActiveSubscriptionId } from '../../../../store/slices/subscription';
import { useNavigate } from 'react-router-dom';
import AssetIcon from '../../../../components/AssetIcon';
import { useFilter } from '../FilterContext';
import './index.css';

// ── Sparkline ────────────────────────────────────────────────────────────────

const Spark: React.FC<{
  dataPoints?: Array<{ value: number; timestamp: number }>;
  profit: boolean;
}> = ({ dataPoints, profit }) => {
  const stroke = profit ? '#00B061' : '#F0162F';
  const fill = profit ? 'rgba(0,176,97,0.16)' : 'rgba(240,22,47,0.16)';

  const { line, area } = useMemo(() => {
    if (!dataPoints?.length || dataPoints.length < 2) {
      return { line: '', area: '' };
    }
    const values = dataPoints.map((d) => d.value);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min || 1;
    const w = 100;
    const h = 30;
    const pad = 2;

    const coords = dataPoints.map((d, i) => {
      const x = (i / (dataPoints.length - 1)) * w;
      const y = h - pad - ((d.value - min) / range) * (h - pad * 2);
      return `${x.toFixed(1)} ${y.toFixed(1)}`;
    });

    const line = coords.map((c, i) => `${i === 0 ? 'M' : 'L'}${c}`).join(' ');
    const area = `${line} L${w} ${h} L0 ${h} Z`;
    return { line, area };
  }, [dataPoints]);

  if (!line) {
    return <div className="bot-ticker-spark-empty" />;
  }

  return (
    <svg
      viewBox="0 0 100 30"
      width="100%"
      height="100%"
      preserveAspectRatio="none"
    >
      <path d={area} fill={fill} stroke="none" />
      <path d={line} stroke={stroke} strokeWidth="1.4" fill="none" />
    </svg>
  );
};

// ── Ticker Item (Design C minimalistic) ──────────────────────────────────────

interface TickerItemProps {
  sub: SubscriptionSummary;
  onPointerDown: (e: React.PointerEvent) => void;
  onPointerMove: (e: React.PointerEvent) => void;
  onPointerUp: (e: React.PointerEvent, subId: number) => void;
}

const TickerItem: React.FC<TickerItemProps> = ({
  sub,
  onPointerDown,
  onPointerMove,
  onPointerUp,
}) => {
  const currentProfit = sub.currentProfit ?? 0;
  const currentPositive = currentProfit >= 0;
  const latestOrderTs = sub.latestTransaction?.transactionTimestamp;
  const latestOrderType = sub.latestTransaction?.orderType;

  const sparkPoints = useMemo<
    Array<{ value: number; timestamp: number }>
  >(() => {
    const cd = (sub as any).chartData?.dataPoints;

    // SELL → show current asset balance series (asset.all);
    // BUY  → show current stable balance series (stable.all).
    // For each, fall back through the most useful data we can show.
    const sellSeries =
      cd?.asset?.all ??
      cd?.asset?.accumulative ??
      cd?.total?.asset ??
      cd?.total?.stable ??
      [];
    const buySeries =
      cd?.stable?.all ?? cd?.stable?.accumulative ?? cd?.total?.stable ?? [];

    const series = latestOrderType === 'SELL' ? sellSeries : buySeries;

    if (!series.length) return [];
    if (!latestOrderTs) return series.slice(-30);

    const filtered = series.filter((d: any) => d.timestamp >= latestOrderTs);
    return filtered.length >= 2 ? filtered : series.slice(-30);
  }, [sub, latestOrderTs, latestOrderType]);

  return (
    <div
      className="bot-ticker-item"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={(e) => onPointerUp(e, sub.id)}
    >
      <div className="bot-ticker-row">
        <AssetIcon
          baseAsset={sub.baseAsset}
          width={20}
          height={20}
          style={{ marginBottom: 0 }}
        />
        <span className="bot-ticker-pair">{sub.baseAsset}</span>
        <span
          className={`bot-ticker-profit ${
            currentPositive
              ? 'bot-ticker-profit--pos'
              : 'bot-ticker-profit--neg'
          }`}
        >
          {formatPct(currentProfit)}
        </span>
      </div>
      <div className="bot-ticker-spark">
        <Spark dataPoints={sparkPoints} profit={currentPositive} />
      </div>
    </div>
  );
};

// ── BotTicker Container ──────────────────────────────────────────────────────

interface BotTickerProps {
  subscriptions: SubscriptionSummary[];
}

const DRAG_THRESHOLD = 6;

const BotTicker: React.FC<BotTickerProps> = ({ subscriptions }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { sortedSubs } = useFilter();
  // Use the sorted/filtered subs from the global FilterContext so the ticker
  // mirrors the leaderboard / subscription list ordering. Fall back to the
  // raw prop when the filter hasn't produced any (initial render edge case).
  const orderedSubs = sortedSubs.length ? sortedSubs : subscriptions;
  const containerRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const offsetRef = useRef(0);
  const halfWidthRef = useRef(0);
  const isHovered = useRef(false);
  const isDragging = useRef(false);
  const pointerId = useRef<number | null>(null);
  const dragStartX = useRef(0);
  const dragStartOffset = useRef(0);
  const didDrag = useRef(false);
  const [, forceMount] = useState(0);

  // Re-measure half-width on layout/resize
  useLayoutEffect(() => {
    const measure = () => {
      const track = trackRef.current;
      if (!track) return;
      halfWidthRef.current = track.scrollWidth / 2;
    };
    measure();
    forceMount((n) => n + 1);
    const obs = new ResizeObserver(measure);
    if (trackRef.current) obs.observe(trackRef.current);
    if (containerRef.current) obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, [orderedSubs]);

  // Smooth transform-based animation
  useEffect(() => {
    if (orderedSubs.length === 0) return;

    const speed = 22; // px/sec
    let last = performance.now();
    let raf = 0;

    const tick = (now: number) => {
      const dt = (now - last) / 1000;
      last = now;
      const track = trackRef.current;
      if (track) {
        if (!isHovered.current && !isDragging.current) {
          offsetRef.current += speed * dt;
        }
        const half = halfWidthRef.current;
        if (half > 0) {
          if (offsetRef.current >= half) offsetRef.current -= half;
          if (offsetRef.current < 0) offsetRef.current += half;
        }
        track.style.transform = `translate3d(${-offsetRef.current}px, 0, 0)`;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [orderedSubs.length]);

  const handlePointerDown = (e: React.PointerEvent) => {
    if (e.button !== undefined && e.button !== 0) return;
    isDragging.current = true;
    didDrag.current = false;
    pointerId.current = e.pointerId;
    dragStartX.current = e.clientX;
    dragStartOffset.current = offsetRef.current;
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isDragging.current || pointerId.current !== e.pointerId) return;
    const delta = e.clientX - dragStartX.current;
    if (Math.abs(delta) > DRAG_THRESHOLD) {
      didDrag.current = true;
      // capture once we've crossed the threshold so subsequent moves outside
      // the original element still feed back to us
      try {
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      } catch {}
    }
    offsetRef.current = dragStartOffset.current - delta;
  };

  const handlePointerUp = (e: React.PointerEvent, subId: number) => {
    const wasDragging = isDragging.current;
    isDragging.current = false;
    if (pointerId.current === e.pointerId) pointerId.current = null;
    try {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {}
    if (!wasDragging || didDrag.current) return;
    // Click — navigate
    dispatch(setActiveSubscriptionId(subId));
    navigate('/sub');
  };

  const handleContainerPointerLeave = () => {
    isHovered.current = false;
  };

  // Duplicate for seamless wrap
  const items = [...orderedSubs, ...orderedSubs];

  return (
    <div
      className="bot-ticker-container"
      ref={containerRef}
      onMouseEnter={() => {
        isHovered.current = true;
      }}
      onMouseLeave={handleContainerPointerLeave}
    >
      <div className="bot-ticker-track" ref={trackRef}>
        {items.map((sub, i) => (
          <TickerItem
            key={`${sub.id}_${i}`}
            sub={sub}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
          />
        ))}
      </div>
    </div>
  );
};

export default BotTicker;
